import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactEventHandler,
} from 'react';

import { Document, Page } from 'react-pdf';
import classNames from 'classnames';
import panzoom, { PanZoom } from 'panzoom';
import { SizeMe, SizeMeProps } from 'react-sizeme';

import { CircularProgress } from '../CircularProgress';
import { Box } from '../Box';
import { Dialog } from '../Dialog';

import { usePanZoomBoundaries } from './hooks';
import { PreviewButtons } from './PreviewButtons';
import { useStyles } from './FilePreviewWithZoom.styles';

const options = {
  isEvalSupported: false,
};

export interface FilePreviewWithZoomProps {
  open: boolean;
  onClose: () => void;
  url: string;
  onLoadFull?: () => Promise<Blob>;
  onLoadSuccess?: () => void;
  withBottomOffset?: boolean;
  fileType?: 'image' | 'pdf';
}

/**
 * Компонент для предварительного просмотра файла с использованием диалогового окна c масштабированием.
 *
 * @param {Object} props - Свойства, которые можно передать в компонент FilePreviewWithZoom.
 * @param {boolean} props.open - Если true, диалоговое окно открыто.
 * @param {boolean} props.withBottomOffset - Если true, то появляется для кнопок снизу.
 * @param {() => void} props.onClose - Функция обратного вызова для обработки закрытия диалогового окна.
 * @param {() => void} [props.onLoadSuccess] - Коллбэк-функция для обработки успешной загрузки файла.
 * @param {string} props.url - URL файла PDF для предварительного просмотра.
 * @returns {JSX.Element} JSX-элемент, представляющий компонент предварительного просмотра файла в масштабируемом диалоговом окне.
 */

export const FilePreviewWithZoom = ({
  open,
  onClose,
  onLoadSuccess,
  onLoadFull,
  url,
  withBottomOffset,
  fileType = 'pdf',
}: FilePreviewWithZoomProps) => {
  const panZoomRef = useRef<PanZoom | null>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const [objectUrl, setObjectUrl] = useState(url);

  const { classes } = useStyles();

  const zoomLevels = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3, 4, 5];
  const [currentZoom, setCurrentZoom] = useState(1);

  const [pagesCount, setPagesCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const { checkBoundaries, setupBoundaryCheck } = usePanZoomBoundaries(
    panZoomRef,
    paperRef,
  );

  useEffect(() => {
    let isCancelled = false;
    let nextObjectUrl = '';

    const loadFile = async () => {
      try {
        if (onLoadFull) {
          const file = await onLoadFull();

          if (!isCancelled) {
            nextObjectUrl = URL.createObjectURL(file);
            setObjectUrl(nextObjectUrl);
          }
        }
      } catch (e) {}
    };

    loadFile();

    return () => {
      isCancelled = true;

      if (nextObjectUrl) {
        URL.revokeObjectURL(nextObjectUrl);
      }
    };
  }, [onLoadFull]);

  const reset = () => {
    if (panZoomRef.current) {
      setCurrentZoom(1);
      setPageNumber(1);
      panZoomRef.current.dispose();
      panZoomRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [url]);

  const zoomTo = (scale: number) => {
    if (!paperRef.current || !panZoomRef.current) return;

    const { offsetWidth, offsetHeight } = paperRef.current;
    const centerX = offsetWidth / 2;
    const centerY = offsetHeight / 2;
    panZoomRef.current.smoothZoomAbs(centerX, centerY, scale);
  };

  const handleLoadSuccess = useCallback(
    ({ numPages }: { numPages?: number }) => {
      setPagesCount(numPages || 1);
      requestAnimationFrame(() => {
        if (!open || !paperRef.current) return;

        panZoomRef.current = panzoom(paperRef.current, {
          maxZoom: 5,
          minZoom: 0.25,
          transformOrigin: { x: 0, y: 0 },
          initialZoom: 1,
          beforeWheel: () => true,
          beforeMouseDown: (event) =>
            !paperRef.current?.contains(event.target as Node),
          smoothScroll: false,
          zoomDoubleClickSpeed: 1,
        });

        onLoadSuccess?.();
      });
    },
    [open, onLoadSuccess, setPagesCount],
  );

  setupBoundaryCheck();

  const handleZoom = (scale: number) => {
    zoomTo(scale);
    setTimeout(() => {
      checkBoundaries();
    }, 300);
    setCurrentZoom(scale);
  };

  if (!open) return null;

  const renderDocument = (size: SizeMeProps['size']) => (
    <Document
      options={options}
      file={url}
      className={classNames(classes.document)}
      error={
        <Box p="32" color="text.light.secondary" textAlign="center">
          Ошибка при загрузке файла
        </Box>
      }
      loading={
        <Box display="flex" justifyContent="center" p="32">
          <CircularProgress size={50} />
        </Box>
      }
      onLoadSuccess={handleLoadSuccess}
    >
      <Page
        key={pageNumber}
        width={size.width === null ? 1 : size.width}
        pageNumber={pageNumber}
        renderAnnotationLayer
      />
    </Document>
  );

  const renderImage = () => (
    <Box>
      <div className={classes.imageWrapper} />
      <img
        className={classNames(classes.image)}
        src={objectUrl}
        alt=""
        onLoad={handleLoadSuccess as ReactEventHandler<HTMLImageElement>}
      />
    </Box>
  );

  return (
    <Box>
      <Dialog
        scroll="body"
        open={open}
        onClose={onClose}
        classes={{
          paper: classes.dialogPaper,
        }}
        PaperProps={{
          ref: paperRef,
        }}
      >
        <SizeMe refreshMode="debounce" refreshRate={100}>
          {({ size }: SizeMeProps) => (
            <Box>
              {fileType === 'pdf' && renderDocument(size)}
              {fileType === 'image' && renderImage()}
            </Box>
          )}
        </SizeMe>
      </Dialog>
      <PreviewButtons
        handleNextPage={setPageNumber}
        pagesCount={pagesCount}
        nextPageNumber={pageNumber}
        pageNumber={pageNumber}
        scale={currentZoom}
        zoomLevels={zoomLevels}
        handleZoom={handleZoom}
        onClose={onClose}
        withBottomOffset={withBottomOffset}
      />
    </Box>
  );
};
